type LngLat = [number, number];
type BoundingBox = [LngLat, LngLat];

interface PointFeatureLike {
  geometry?: { type?: string; coordinates?: unknown };
  properties?: Record<string, unknown> | null;
}

const SINGLE_POINT_PADDING_DEG = 0.02;

function isLngLat(value: unknown): value is LngLat {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === "number" &&
    typeof value[1] === "number" &&
    Number.isFinite(value[0]) &&
    Number.isFinite(value[1])
  );
}

export function computeBounds(
  features: PointFeatureLike[],
): BoundingBox | null {
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;
  let count = 0;

  for (const feature of features) {
    if (feature.geometry?.type !== "Point") continue;
    const coords = feature.geometry.coordinates;
    if (!isLngLat(coords)) continue;
    const [lon, lat] = coords;
    if (lon < minLon) minLon = lon;
    if (lat < minLat) minLat = lat;
    if (lon > maxLon) maxLon = lon;
    if (lat > maxLat) maxLat = lat;
    count++;
  }

  if (count === 0) return null;

  if (minLon === maxLon && minLat === maxLat) {
    return [
      [minLon - SINGLE_POINT_PADDING_DEG, minLat - SINGLE_POINT_PADDING_DEG],
      [maxLon + SINGLE_POINT_PADDING_DEG, maxLat + SINGLE_POINT_PADDING_DEG],
    ];
  }

  return [
    [minLon, minLat],
    [maxLon, maxLat],
  ];
}

const SEARCHABLE_KEYS = [
  "city",
  "state",
  "postal_code",
  "street_line_1",
  "formatted_address",
  "assigned_rep_name",
  "territory_name",
  "location_type",
] as const;

export function buildSearchIndex(
  properties: Record<string, unknown> | null | undefined,
): string {
  if (!properties) return "";
  const parts: string[] = [];
  for (const key of SEARCHABLE_KEYS) {
    const value = properties[key];
    if (typeof value === "string" && value.length > 0) parts.push(value);
  }
  const customerNames = properties.customer_names;
  if (Array.isArray(customerNames)) {
    for (const name of customerNames) {
      if (typeof name === "string" && name.length > 0) parts.push(name);
    }
  }
  return parts.join(" ").toLowerCase();
}

export function filterFeatures<T extends PointFeatureLike>(
  features: T[],
  query: string,
): T[] {
  const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return features;
  return features.filter((feature) => {
    const index = buildSearchIndex(feature.properties);
    return tokens.every((token) => index.includes(token));
  });
}
